import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useBranding } from '@/contexts/BrandingContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';
import api from '@/lib/api';

export default function LoginPage() {
  const { login, register } = useAuth();
  const { branding } = useBranding();
  const studioName = branding.accountName || 'Interior Story';
  const initials = studioName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || 'IS';
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '' });

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: form.email });
      toast.success('If that email exists, a reset link has been sent.');
      setForgotMode(false);
    } catch {
      toast.error('Could not process request');
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(form.email, form.password);
        toast.success('Welcome back');
      } else {
        await register(form.name, form.email, form.password);
        toast.success('Account created');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === 'pending_approval') {
        toast.success('Request sent! The studio owner will review your access request.');
        setIsLogin(true);
      } else {
        toast.error(detail || 'Something went wrong');
      }
    } finally {
      setLoading(false);
    }
  };

  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  const handleGoogleLogin = () => {
    // Standard Google OAuth 2.0 authorization-code flow. redirect_uri must be
    // registered as an "Authorized redirect URI" on the Google Cloud OAuth
    // client (see backend/.env.example) and must match AuthCallback.js exactly.
    const redirectUri = `${window.location.origin}/auth/google/callback`;
    const state = window.crypto.randomUUID();
    sessionStorage.setItem('google_oauth_state', state);
    const params = new URLSearchParams({
      client_id: googleClientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid email profile',
      access_type: 'online',
      prompt: 'select_account',
      state,
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex" data-testid="login-page">
      {/* Left panel - decorative */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#2C2C2E] relative overflow-hidden items-end p-12">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'url(https://images.unsplash.com/photo-1649083048391-1c9e82472f65?w=1200)', backgroundSize: 'cover', backgroundPosition: 'center'}} />
        <div className="relative z-10">
          <div className="flex items-center gap-2.5 mb-8">
            {branding.logoUrl ? (
              <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center overflow-hidden p-1">
                <img src={branding.logoUrl} alt={studioName} className="max-w-full max-h-full object-contain" data-testid="login-logo" />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-xl bg-[#3A8C8C] flex items-center justify-center">
                <span className="text-white font-bold font-['Manrope'] text-lg">{initials}</span>
              </div>
            )}
            <span className="text-white font-semibold text-xl font-['Manrope'] tracking-tight">{studioName}</span>
          </div>
          <h1 className="text-white text-4xl font-['Manrope'] font-bold leading-tight mb-4">
            Design your space.<br />Manage your studio.
          </h1>
          <p className="text-white/60 text-base max-w-md leading-relaxed">
            The calm, sophisticated ERP for interior design studios. From leads to handover, everything in one place.
          </p>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-sm animate-fade-in-up">
          <div className="lg:hidden flex items-center gap-2.5 mb-10">
            {branding.logoUrl ? (
              <div className="w-8 h-8 rounded-lg bg-white border border-[#E8E6E1] flex items-center justify-center overflow-hidden p-0.5">
                <img src={branding.logoUrl} alt={studioName} className="max-w-full max-h-full object-contain" />
              </div>
            ) : (
              <div className="w-8 h-8 rounded-lg bg-[#3A8C8C] flex items-center justify-center">
                <span className="text-white text-sm font-bold font-['Manrope']">{initials}</span>
              </div>
            )}
            <span className="font-semibold text-[#2C2C2E] text-base font-['Manrope']">{studioName}</span>
          </div>

          <h2 className="text-2xl font-['Manrope'] font-bold text-[#2C2C2E] mb-1">
            {forgotMode ? 'Forgot password' : isLogin ? 'Welcome back' : 'Create account'}
          </h2>
          <p className="text-sm text-[#6B7280] mb-8">
            {forgotMode ? "Enter your email and we'll send a reset link" : isLogin ? 'Sign in to your studio dashboard' : 'Set up your studio workspace'}
          </p>

          {forgotMode && (
            <form onSubmit={handleForgot} className="space-y-5" data-testid="forgot-form">
              <div className="space-y-2">
                <Label className="text-xs font-medium text-[#6B7280] uppercase tracking-wide">Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="you@studio.com" className="h-11 bg-white border-[#D4D0C8] rounded-xl" required data-testid="forgot-email-input" />
              </div>
              <button type="submit" disabled={loading} data-testid="forgot-submit-btn" className="w-full h-11 bg-[#3A8C8C] text-white rounded-full font-medium text-sm hover:bg-[#2E7A7A] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <>Send Reset Link <ArrowRight size={16} strokeWidth={1.5} /></>}
              </button>
              <button type="button" onClick={() => setForgotMode(false)} className="w-full text-sm text-[#6B7280] hover:text-[#3A8C8C] transition-colors">Back to sign in</button>
            </form>
          )}

          <form onSubmit={handleSubmit} className={forgotMode ? 'hidden' : 'space-y-5'}>
            {!isLogin && (
              <div className="space-y-2">
                <Label className="text-xs font-medium text-[#6B7280] uppercase tracking-wide">Name</Label>
                <Input
                  data-testid="register-name-input"
                  value={form.name}
                  onChange={e => setForm({...form, name: e.target.value})}
                  placeholder="Your name"
                  className="h-11 bg-white border-[#D4D0C8] rounded-xl focus:ring-[#3A8C8C]/20 focus:border-[#3A8C8C]"
                  required
                />
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-xs font-medium text-[#6B7280] uppercase tracking-wide">Email</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={form.email}
                onChange={e => setForm({...form, email: e.target.value})}
                placeholder="you@studio.com"
                className="h-11 bg-white border-[#D4D0C8] rounded-xl focus:ring-[#3A8C8C]/20 focus:border-[#3A8C8C]"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-medium text-[#6B7280] uppercase tracking-wide">Password</Label>
              <div className="relative">
                <Input
                  data-testid="login-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={e => setForm({...form, password: e.target.value})}
                  placeholder="Enter password"
                  className="h-11 bg-white border-[#D4D0C8] rounded-xl pr-10 focus:ring-[#3A8C8C]/20 focus:border-[#3A8C8C]"
                  required
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#2C2C2E]">
                  {showPassword ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
                </button>
              </div>
              {isLogin && (
                <button type="button" onClick={() => setForgotMode(true)} data-testid="forgot-password-link" className="text-xs text-[#6B7280] hover:text-[#3A8C8C] transition-colors">
                  Forgot password?
                </button>
              )}
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-11 bg-[#3A8C8C] text-white rounded-full font-medium text-sm hover:bg-[#2E7A7A] transition-colors active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <>{isLogin ? 'Sign In' : 'Create Account'} <ArrowRight size={16} strokeWidth={1.5} /></>}
            </button>
          </form>

          {!forgotMode && googleClientId && (
            <div className="mt-5">
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-[#E8E6E1]" />
                <span className="text-xs text-[#9CA3AF]">or</span>
                <div className="flex-1 h-px bg-[#E8E6E1]" />
              </div>
              <button
                type="button"
                onClick={handleGoogleLogin}
                data-testid="google-login-btn"
                className="w-full h-11 bg-white border border-[#D4D0C8] text-[#2C2C2E] rounded-full font-medium text-sm hover:bg-[#FAFAF8] hover:border-[#3A8C8C]/40 transition-colors active:scale-[0.98] flex items-center justify-center gap-3"
              >
                <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                  <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.71-1.57 2.68-3.89 2.68-6.62z"/>
                  <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.85.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"/>
                  <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z"/>
                  <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.47.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"/>
                </svg>
                Continue with Google
              </button>
            </div>
          )}

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              data-testid="toggle-auth-mode"
              className={`text-sm text-[#6B7280] hover:text-[#3A8C8C] transition-colors ${forgotMode ? 'hidden' : ''}`}
            >
              {isLogin ? "Don't have an account? Create one" : "Already have an account? Sign in"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
